import {Text} from "@gs-ux-uitoolkit-react/text";
import {Input} from '@gs-ux-uitoolkit-react/input';
import {useEffect, useState} from "react";
import {Button} from "@gs-ux-uitoolkit-react/button";
import {Header} from "../../../shared/components/Header";
import {Footer} from "../../../shared/components/Footer";
import {FormInput} from "../../../shared/components/FormInput";
import {FormSelectInput} from "../../../shared/components/FormSelectInput";
import {FormControl, FormLabel } from "@gs-ux-uitoolkit-react/form";
import {FormPhoneInput} from "../../../shared/components/FormPhoneInput";
import {CountryCode} from "libphonenumber-js";
import {FormDateInput} from "../../../shared/components/FormDateInput";
import {LegacyIcon} from "@gs-ux-uitoolkit-react/icon-font";
import {US_STATES} from "./utils";
import {userProfileErrors, validateClientInformationForm} from "./AdvisorValidations";
import {toastService} from "@gs-ux-uitoolkit-react/toast";
import {useNavigate, useParams} from "react-router-dom";
import axios from "axios";
import _ from "lodash";

const CreateUserTaskTitleEnum: Record<string, UserTitleEnum> = {
    Dr: "DR",
    Miss: "MISS",
    Mr: "MR",
    Mrs: "MRS",
    Ms: "MS"
};

type UserTitleEnum = "DR" | "MISS" | "MR" | "MRS" | "MS" | "";
type UserSuffixEnum = string;

const suffixOptions: { value: UserSuffixEnum; label: string }[] = [
    { value: "Jr", label: "Jr" },
    { value: "Sr", label: "Sr" },
    { value: "III", label: "III" },
    { value: "IV", label: "IV" }
];

const NAME_REGEXP: RegExp = /^(?=.*[a-zA-Z])[a-zA-Z\s-.']+$/;


export function AdvisorOnboardingForm() {
    
    const [isThirdPartyAgent, setIsThirdPartyAgent] = useState(false);
    const [isAdvisorCreated, setIsAdvisorCreated] = useState(false);
    const [title, setTitle] = useState("");
    const [firstName, setFirstName] = useState('');
    const [middleName, setMiddleName] = useState('');
    const [lastName, setLastName] = useState('');
    const [suffix, setSuffix] = useState('');
    const [loginID, setLoginID] = useState('');
    const [email1, setEmail1] = useState('');
    const [email2, setEmail2] = useState('');
    const [primaryPhoneCountry, setPrimaryPhoneCountry] = useState<CountryCode>("US");
    const [secondaryPhoneCountry, setSecondaryPhoneCountry] = useState<CountryCode>("US");
    const [workTelephone, setWorkTelephone] = useState('');
    const [homeTelephone, setHomeTelephone] = useState('');
    const [dateOfBirth, setDateOfBirth] = useState('');
    const [tid, setTid] = useState('');
    const [address1, setaddress1] = useState('');
    const [address2, setaddress2] = useState('');
    const [address3] = useState('');
    const [city, setCity] = useState('');
    const [usState, setUsState] = useState('');
    const [zip, setZip] = useState('');
    const [isMasked, setIsMasked] = useState(true);
    const [errors, setErrors] = useState<Record<string, string | null>>({});
    const [loginIdValidationMessage, setLoginIdValidationMessage] = useState<string | null>(null);
    const navigate = useNavigate();
    const { repId } = useParams<{ repId: string }>(); // Extract repId from the URL
    const [errorsAfterValidationForm, setErrorsAfterValidationForm] = useState({});
    const [linkDeactivatedError, setLinkDeactivatedError] = useState<string | null>(null);
    
    useEffect(() => {
        const checkCookieAndCleanForm = () => {
                // If the cookie is expired or not present, clean the form
                setTitle("");
                setFirstName("");
                setMiddleName("");
                setLastName("");
                setSuffix("");
                setLoginID("");
                setEmail1("");
                setEmail2("");
                setPrimaryPhoneCountry("US");
                setSecondaryPhoneCountry("US");
                setWorkTelephone("");
                setHomeTelephone("");
                setDateOfBirth("");
                setTid("");
                setaddress1("");
                setaddress2("");
                setCity("");
                setUsState("");
                setZip("");
                setErrors({});
                setLoginIdValidationMessage(null);
                toastService.show({
                    status: 'error',
                    message: "Your session has expired. Please start over.",
                    placement: "top",
                    dismissible: true,
                    className: "top-20",
                    autoDismiss: 3_500,
                });
        };

        // Set an interval to check the cookie every 15 minutes
        const intervalId = setInterval(() => {
            checkCookieAndCleanForm();
        }, 15 * 60 * 1000); // 15 minutes in milliseconds

        // Cleanup the interval on component unmount
        return () => clearInterval(intervalId);
    }, []); // Run only once when the component mounts

    useEffect(() => {
        const fetchData = async () => {
            if (repId) {
                let url = `/api/v2/advisors-invitations/${repId}/firm-approved-rep`;
                try {
                    const response = await axios.get(url);
                    if (response.data?.invitationstatus === 'password&secretQuestionAnswerCreated') {
                        navigate(`/advisor-invitations/${repId}/create-password`);
                        return;
                    }
                    response.data.issma === 'Y' ? setIsThirdPartyAgent(true) : setIsThirdPartyAgent(false);
                    setFirstName(response.data.firstname);
                    setLastName(response.data.lastname);
                    setEmail1(response.data.email);
                    setWorkTelephone(response.data.phonenumber);
                    if (response.data.invitationstatus === 'advisorCreated') {
                        setIsAdvisorCreated(true);
                    }
                } catch (error: any) {
                    if (error?.response?.status === 401) {
                        navigate(`/advisor-invitations/mfa-attempts/${repId}`);
                    } else if (error?.response?.data?.displayMessage) {
                        setLinkDeactivatedError(error.response.data.displayMessage);
                    }
                }
            }
        };

        fetchData();
    }, [repId, navigate]);


    const validateLoginId = async () => {
        if (!loginID) {
            setLoginIdValidationMessage(null);
            return;
        }
        
        const url = `/api/v2/advisors-invitations/${repId}/validate-login/${loginID}`;
        try {
            const response = await axios.get(url);
            if (!response.data) {
                setLoginIdValidationMessage("This username is already taken. Please choose another.");
            } else {
                setLoginIdValidationMessage("This username is available.");
            }
        } catch (error) {
            setLoginIdValidationMessage("An error occurred while validating the username.");
        }
    };

    const handleBlur = (fieldName: string) => {
        const validationErrors = userProfileErrors(advisorRequest);
        setErrors((prevErrors) => ({
            ...prevErrors,
            [fieldName]: validationErrors[fieldName]?.displayMessage || null
        }));
    };

    const updateDateOfBirth = (newDate: string) => {
        _.set(advisorRequest, 'dateOfBirth', newDate);
        setDateOfBirth(newDate);
    };

    const advisorRequest = {
        isThirdPartyAgent,
        title,
        firstName,
        middleName,
        lastName,
        suffix,
        loginID,
        email1,
        email2,
        workTelephone,
        repId: repId, 
        ...(isThirdPartyAgent === false && {
            homeTelephone,
            dateOfBirth,
            tid,
            hAddress: {
                address1,
                address2,
                address3,
                city,
                state: usState,
                zip
            }
        })
    };

    const isEmptyObject = (obj: object) => {
        return Object.keys(obj).length === 0;
    };

    const handleSave = () => {
        // Perform form validation
        const error = validateClientInformationForm(advisorRequest);
        console.log("Error", error);
        if (isEmptyObject(error)) {
            createAdvisor();
        } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            setErrorsAfterValidationForm(error);
            toastService.show({
                status: 'error',
                message: `Please enter all the required details`,
                placement: 'top',
                dismissible: true,
                className: 'top-20',
                autoDismiss: 3_500
            });
        }
    };


    function createAdvisor() {
        const url = `/api/v2/advisors-invitations/${repId}/create`;
        
        axios.put(url, advisorRequest)
            .then((response) => {
                console.log(response);
                navigate(`/advisor-invitations/${repId}/create-password`);
            })
            .catch((error) => {
                // Format timestamp if present, including timezone
                let formattedTimestamp = "";
                if (error?.response?.data?.timestamp) {
                    try {
                        const date = new Date(error.response.data.timestamp);
                        if (!isNaN(date.getTime())) {
                            // Example: 7/15/2025, 10:13:43 AM GMT-4
                            formattedTimestamp = date.toLocaleString(undefined, {
                                timeZoneName: "short"
                            });
                        } else {
                            formattedTimestamp = error.response.data.timestamp;
                        }
                    } catch {
                        formattedTimestamp = error.response.data.timestamp;
                    }
                }
                
                if(error?.response?.data?.type === "VALIDATION_ERROR") { toastService.show({
                    status: 'error',
                    message: `Your identity can not be validated with the information provided. Please contact support and share the Request ID: ${error?.response?.data?.requestId}. `,
                    placement: 'top',
                    dismissible: true,
                    className: 'top-20'
                });}else{ 
                    toastService.show({
                    status: 'error',
                    message: `Failed to create Advisor${formattedTimestamp ? " at " + formattedTimestamp : ""}! Please contact support and share the Request ID: ${error?.response?.data?.requestId}. `,
                    placement: 'top',
                    dismissible: true,
                    className: 'top-20'
                });
                }
            });
    }

    return (
        <>
            <Header />
            {linkDeactivatedError === "Your account is locked due to too many failed attempts. Please contact support." ? (
                <div className="bg-red-10 p-4 rounded w-full max-w-screen-md mx-auto mt-4">
                    <Text typography="textBody02Medium" color="red060" className="mb-2">
                        Error
                    </Text>
                    <Text typography="textBody02Regular" color="red060">
                        {linkDeactivatedError}
                    </Text>
                </div>
            ) : (
                <>
                    {errorsAfterValidationForm && Object.keys(errorsAfterValidationForm).length > 0 && (
                        <div className="justify-between flex-1 flex flex-col min-[700px]:flex-row items-center px-16 xl:gap-72 lg:gap-24 md:gap-12 sm:gap-6">
                            <div className="self-stretch px-6 py-6 inline-flex justify-center items-center gap-2.5">
                                <div className="text-red-700 mb-4 rounded">
                                <p className="font-bold">Errors:</p>
                                    <ul className="list-disc pl-5">
                                        {Object.entries(errorsAfterValidationForm).map(([key, value]) => (
                                            <li key={key}>
                                                {typeof value === 'object' && value !== null ? (
                                                    Object.entries(value).map(([subKey, subValue]) => (
                                                        <span key={subKey} className="block">
                                                            {subValue}
                                                        </span>
                                                    ))
                                                ) : (
                                                    ` ${value}`
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="justify-between flex-1 flex flex-col min-[700px]:flex-row items-center px-16 xl:gap-72 lg:gap-24 md:gap-12 sm:gap-6">
                        <div className="self-stretch px-6 py-6 inline-flex justify-center items-center gap-2.5">
                            <Text typography="textSubtitle03Medium">To complete your user account set up, please complete the following information</Text>
                        </div>
                    </div>
                    {isAdvisorCreated ? (
                        <div className="justify-between flex-1 flex flex-col items-center px-16 py-16">
                            <Text typography="textBody01Regular">
                                Advisor has already been created. Please proceed to{" "}
                                <a
                                    onClick={() => navigate(`/advisor-invitations/${repId}/create-password`)}
                                    style={{ cursor: 'pointer', color: 'blue' }}
                                >
                                    Create Password
                                </a>.
                            </Text>
                        </div>
                    ) : (
                        <>
                            <div className="justify-between flex-1 flex flex-col min-[700px]:flex-row items-center px-16 xl:gap-72 lg:gap-24 md:gap-12 sm:gap-6">
                                <div className="self-stretch px-6 py-6 inline-flex justify-center items-center gap-2.5">
                                    <Text typography="textBody04Regular">User Information</Text>
                                </div>
                            </div>
                            <div className="justify-between flex-1 flex flex-col min-[700px]:flex-row items-center px-16 xl:gap-72 lg:gap-24 md:gap-12 sm:gap-6">
                                <div className="self-stretch px-6 inline-flex justify-center items-center gap-2.5">
                                    <div className="flex flex-row gap-4 w-full grid grid-cols-1 md:grid-cols-6 lg:grid-cols-6 xl:grid-cols-6 max-w-screen-2xl mb-6">
                                        <FormSelectInput
                                            className="max-w-xs"
                                            options={Object.entries(CreateUserTaskTitleEnum).map(
                                                ([key, val]) => ({
                                                    value: val,
                                                    label: key
                                                })
                                            )}
                                            label="Title"
                                            name={`title`}
                                            value={title}
                                            setValue={(newVal) => setTitle(newVal as UserTitleEnum)}
                                        />
                                        <FormControl>
                                            <FormInput
                                                isRequired
                                                className="max-w-xs"
                                                label={"First Name"}
                                                name={`firstName`}
                                                value={firstName}
                                                error={errors.firstName ?? undefined}
                                                setValue={(newVal) => setFirstName(newVal)}
                                                onBlur={() => handleBlur("firstName")}
                                            />
                                        </FormControl>
                                        <FormControl>
                                            <FormInput
                                                className="max-w-xs"
                                                label={"Middle Name"}
                                                name={`middleName`} // not seeing middle name in the service task item
                                                value={middleName}
                                                error={errors.middleName ?? undefined}
                                                setValue={(newVal) => setMiddleName(newVal)}
                                                allowedRegex={NAME_REGEXP}
                                                onBlur={() => handleBlur("middleName")}
                                            />
                                        </FormControl>
                                        <FormControl>
                                            <FormInput
                                                isRequired
                                                className="max-w-xs"
                                                label={"Last Name"}
                                                name={`lastName`}
                                                value={lastName}
                                                setValue={(newVal) => setLastName(newVal)}
                                                allowedRegex={NAME_REGEXP}
                                                error={errors.lastName ?? undefined}
                                                onBlur={() => handleBlur("lastName")}
                                            />
                                        </FormControl>
                                        <FormControl>
                                            <FormSelectInput
                                                className="max-w-xs"
                                                options={suffixOptions}
                                                label={"Suffix"}
                                                name={`suffix`}
                                                value={suffix}
                                                setValue={(newVal) => setSuffix(newVal)}
                                            />
                                        </FormControl>
                                    </div>
                                </div>
                            </div>
                            <div className="justify-between flex-1 flex flex-col min-[700px]:flex-row items-center px-16 xl:gap-72 lg:gap-24 md:gap-12 sm:gap-6">
                                <div className="self-stretch px-6 inline-flex justify-center items-center gap-2.5 md:min-w-[800px]">
                                    <div className="flex flex-row gap-4 w-full py-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 max-w-screen-2xl mb-6">
                                        <FormInput
                                            isRequired
                                            className="max-w-xs"
                                            name={`loginID`}
                                            label={"Username"}
                                            helperText={"The username must be between 8-32 characters long, cannot contain symbols or spaces, and is not case sensitive. It is permanent and cannot be changed."}
                                            value={loginID}
                                            setValue={(newVal) => setLoginID(newVal)}
                                            allowedRegex={/^[a-z0-9]+$/}
                                            error={errors.loginID ?? undefined}
                                            onBlur={() => {
                                                handleBlur("loginID");
                                                validateLoginId(); // Call validateLoginId on blur
                                            }}
                                        />
                                        {loginIdValidationMessage && (
                                            <Text typography="textCaption01Regular" className="mb-6 mt-6" color={loginIdValidationMessage.includes("available") ? "green060" : "red060"}>
                                                {loginIdValidationMessage}
                                            </Text>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="justify-between flex-1 flex flex-col min-[700px]:flex-row items-center px-16 xl:gap-72 lg:gap-24 md:gap-12 sm:gap-6">
                                <div className="self-stretch px-6 inline-flex justify-center items-center gap-2.5 md:min-w-[800px]">
                                    <div className="flex flex-row gap-4 w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 max-w-screen-2xl mb-6">
                                        <FormInput
                                            name={'email1'}
                                            label={"Default Email Address"}
                                            value={email1}
                                            setValue={(newVal) => setEmail1(newVal)}
                                            error={errors.email1 ?? undefined}
                                            onBlur={() => handleBlur("email")}
                                            disabled={true}
                                        />
                                        <FormInput
                                            name={'email2'}
                                            label={"Recovery Email Address"}
                                            value={email2}
                                            setValue={(newVal) => setEmail2(newVal)}
                                            error={errors.email2 ?? undefined}
                                            onBlur={() => handleBlur("recoveryEmailAddress")}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="justify-between flex-1 flex flex-col min-[700px]:flex-row items-center px-16 xl:gap-72 lg:gap-24 md:gap-12 sm:gap-6">
                                <div className="self-stretch px-6 inline-flex justify-center items-center gap-2.5 md:min-w-[800px]">
                                    <div className="flex flex-row gap-4 w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 max-w-screen-2xl mb-6">
                                        
                                        <FormControl>
                                            <FormLabel><b>Primary Phone *</b></FormLabel>
                                            <FormPhoneInput
                                                countryCode={primaryPhoneCountry}
                                                onCountryCodeChange={setPrimaryPhoneCountry}
                                                name={`dayPhoneNumber`}
                                                helperText="This number will be used for 2 Factor Authentication"
                                                value={workTelephone}
                                                setValue={(newVal) => setWorkTelephone(newVal)}
                                                error={errors.workTelephone ?? undefined}
                                                onBlur={() => handleBlur("workTelephone")}
                                                disabled={true}
                                            />
                                        </FormControl>
                                        
                                        {!isThirdPartyAgent && (
                                            <FormControl>
                                                <FormLabel><b>Secondary Phone</b></FormLabel>
                                                <FormPhoneInput
                                                    countryCode={secondaryPhoneCountry}
                                                    onCountryCodeChange={setSecondaryPhoneCountry}
                                                    name={`eveningPhoneNumber`}
                                                    helperText="This number will be used for 2 Factor Authentication"
                                                    value={homeTelephone}
                                                    setValue={(newVal) => setHomeTelephone(newVal)}
                                                    error={errors.eveningPhoneNumber ?? undefined}
                                                    onBlur={() => handleBlur("eveningPhoneNumber")}
                                                />
                                            </FormControl>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {!isThirdPartyAgent && (
                                <>
                                    <div className="justify-between flex-1 flex flex-col min-[700px]:flex-row items-center px-16 xl:gap-72 lg:gap-24 md:gap-12 sm:gap-6">
                                        <div className="self-stretch px-6 inline-flex justify-center items-center gap-2.5 md:min-w-[800px]">
                                            <div className="flex flex-row gap-4 w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 max-w-screen-2xl mb-6">

                                                <FormControl>
                                                    <FormDateInput
                                                        isRequired
                                                        label="Date of Birth"
                                                        name={`dateOfBirth`}
                                                        dateFormat="m/d/Y"
                                                        maxLength={10}
                                                        value={dateOfBirth}
                                                        setValue={(newVal) => updateDateOfBirth(newVal ?? '')}
                                                        error={errors.dateOfBirth ?? undefined}
                                                        onBlur={() => handleBlur("dateOfBirth")}
                                                    />
                                                </FormControl>
                                                <FormControl className="mb-0">
                                                    <Text typography="textBody02Medium" className="mb-1" color="colorTextNeutralBold">
                                                        <label htmlFor="tid">SSN or Tax ID Number *</label>
                                                    </Text>
                                                    <Input
                                                        value={tid}
                                                        inputId={"tid"}
                                                        name={`tid`}
                                                        type={isMasked ? "password" : "text"}
                                                        onChange={(e) => {
                                                            if (e.target.value.length <= 9 && /^\d*$/.test(e.target.value)) {
                                                                setTid(e.target.value);
                                                            }
                                                        }}
                                                        status={errors.tid ? "error" : undefined}

                                                        trailingContent={
                                                            <div className="pointer-events-auto" style={{ marginRight: '5px', cursor: 'pointer' }} onClick={() => setIsMasked(!isMasked)}>
                                                                <LegacyIcon
                                                                    name={isMasked ? "visibility" : "visibility-off"}
                                                                    type="filled"
                                                                />
                                                            </div>
                                                        }
                                                        onBlur={() => handleBlur("tid")}
                                                    />
                                                    {errors.tid && (
                                                        <Text typography="textCaption01Regular" color="red060" className="mt-1">
                                                            {errors.tid}
                                                        </Text>
                                                    )}
                                                </FormControl>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="justify-between flex-1 flex flex-col min-[700px]:flex-row items-center px-16 xl:gap-72 lg:gap-24 md:gap-12 sm:gap-6">
                                        <div className="self-stretch px-6 py-4 inline-flex justify-center items-center gap-2.5">
                                            <Text typography="textBody04Regular">Primary Residential Address</Text>
                                        </div>
                                    </div>
                                    <div className="justify-between flex-1 flex flex-col min-[700px]:flex-row items-center px-16 xl:gap-72 lg:gap-24 md:gap-12 sm:gap-6">
                                        <div className="self-stretch px-6 inline-flex justify-center items-center gap-2.5 md:min-w-[800px]">
                                            <div className="flex flex-row gap-4 w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 max-w-screen-2xl mb-6">
                                                <div className="grid grid-cols-12 gap-x-6 mt-4 space-y-4">
                                                    <div className="col-span-12 lg:col-span-6">
                                                        <FormInput
                                                            isRequired
                                                            label="Address 1"
                                                            name={`address1`}
                                                            helperText={"Street address only, no P.O. Boxes"}
                                                            allowedRegex={/^[A-Za-z0-9. ]+$/}
                                                            value={address1}
                                                            setValue={(newVal) => setaddress1(newVal)}
                                                            error={errors.address1 ?? undefined}
                                                            onBlur={() => handleBlur("address1")}
                                                        />
                                                    </div>
                                                    <div className="col-span-12 lg:col-span-6 lg:col-start-1">
                                                        <FormInput
                                                            label="Address 2"
                                                            name={`address2`}
                                                            allowedRegex={/^[A-Za-z0-9. ]+$/}
                                                            value={address2}
                                                            setValue={(newVal) => setaddress2(newVal)}
                                                            error={errors.address2 ?? undefined}
                                                            onBlur={() => handleBlur("address2")}
                                                        />
                                                    </div>
                                                    <div className="col-span-12 lg:col-start-1 lg:col-span-4">
                                                        <FormInput
                                                            isRequired
                                                            label="City"
                                                            name={`city`}
                                                            allowedRegex={/^[A-Za-z\- ]+$/}
                                                            value={city}
                                                            setValue={(newVal) => setCity(newVal)}
                                                            error={errors.city ?? undefined}
                                                            onBlur={() => handleBlur("city")}
                                                        />
                                                    </div>
                                                    <div className="col-span-6 lg:col-span-4 xl:col-span-3">
                                                        <FormSelectInput
                                                            isRequired
                                                            label="State"
                                                            options={Object.entries(US_STATES).map(([key, value]) => ({
                                                                value: key,
                                                                label: value,
                                                            }))}
                                                            setValue={(newVal) => setUsState(newVal)}
                                                            value={usState}
                                                            name={"State"}
                                                            error={errors.usState ?? undefined}
                                                            onBlur={() => handleBlur("usState")}
                                                        />
                                                    </div>

                                                    <div className="col-span-6 lg:col-span-4">
                                                        <FormInput
                                                            isRequired
                                                            label="Zip or Postal Code"
                                                            name={`zipcode`}
                                                            allowedRegex={/^[0-9]{0,5}(?:-[0-9]{0,4})?$/}
                                                            setValue={(newVal) => setZip(newVal)}
                                                            value={zip}
                                                            error={errors.zip ?? undefined}
                                                            onBlur={() => handleBlur("zip")}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                            <div className="col-span-12 flex justify-end mt-4 px-6 py-4 bg-gray-10">
                                <Button
                                    className="bg-blue-60 rounded"
                                    size="lg"
                                    type="submit"
                                    onClick={handleSave} 
                                    appearance="neutral"
                                >
                                    Next: Create Password
                                </Button>
                            </div>
                        </>
                    )}
                </>
            )}
            <Footer />
        </>
    );
}